import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { FaTimes } from 'react-icons/fa';
import { listCalls } from './twilioCall';
import AudioPlayer from './CallAudioPlayer';

const formatDuration = (secs) => {
  if (!secs || secs <= 0) return '—';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const STATUS_STYLES = {
  completed: { color: 'var(--state-success-text)', background: 'var(--state-success-bg)' },
  'in-progress': { color: 'var(--state-success-text)', background: 'var(--state-success-bg)' },
  answered: { color: 'var(--state-success-text)', background: 'var(--state-success-bg)' },
  ringing: { color: 'var(--state-warning-text)', background: 'var(--state-warning-bg)' },
  initiated: { color: 'var(--state-warning-text)', background: 'var(--state-warning-bg)' },
  busy: { color: 'var(--state-error-text)', background: 'var(--state-error-bg)' },
  failed: { color: 'var(--state-error-text)', background: 'var(--state-error-bg)' },
  'no-answer': { color: 'var(--state-error-text)', background: 'var(--state-error-bg)' },
  'not-answered': { color: 'var(--state-error-text)', background: 'var(--state-error-bg)' },
  canceled: { color: 'var(--text-secondary)', background: 'var(--border-default)' }
};

function StatusPill({ status, answered }) {
  // A child-leg "completed" only means "picked up and hung up" when answered=1.
  // Otherwise the call ended before pickup — show that explicitly.
  let display = status;
  if (status === 'completed' && !answered) display = 'not-answered';

  const style = STATUS_STYLES[display] ||
    STATUS_STYLES[status] || {
      color: 'var(--text-secondary)',
      background: 'var(--border-default)'
    };
  return (
    <span
      style={{
        ...style,
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: '9999px',
        fontSize: '12px',
        fontWeight: 500,
        textTransform: 'capitalize'
      }}
    >
      {display || '—'}
    </span>
  );
}

function CallHistoryModal({ studentUuid, onClose }) {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await listCalls(studentUuid);
        if (!cancelled) setCalls(rows || []);
      } catch (e) {
        if (!cancelled) setError('Failed to load call history');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studentUuid]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--overlay-modal-strong)',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px'
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface-card)',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '1100px',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <div style={{ fontSize: '16px', fontWeight: 600, color: 'var(--text-primary)' }}>
            Call History
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '9999px',
              border: 'none',
              background: 'var(--surface-sunken)',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0
            }}
          >
            <FaTimes size={14} />
          </button>
        </div>

        <div style={{ overflow: 'auto', padding: '12px 20px 20px' }}>
          {loading && <div style={{ padding: '24px', color: 'var(--text-muted)' }}>Loading…</div>}
          {error && <div style={{ padding: '24px', color: 'var(--state-error)' }}>{error}</div>}
          {!loading && !error && calls.length === 0 && (
            <div style={{ padding: '24px', color: 'var(--text-muted)' }}>No calls yet.</div>
          )}
          {!loading && !error && calls.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr
                  style={{
                    textAlign: 'left',
                    color: 'var(--text-muted)',
                    borderBottom: '1px solid #e5e7eb'
                  }}
                >
                  <th style={{ padding: '8px 8px' }}>Date & Time</th>
                  <th style={{ padding: '8px 8px' }}>Status</th>
                  <th style={{ padding: '8px 8px' }}>Duration</th>
                  <th style={{ padding: '8px 8px' }}>Direction</th>
                  <th style={{ padding: '8px 8px' }}>CMS User</th>
                  <th style={{ padding: '8px 8px' }}>Recording</th>
                </tr>
              </thead>
              <tbody>
                {calls.map((call) => (
                  <tr key={call.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>
                      {moment(call.created_at).format('lll')}
                    </td>
                    <td style={{ padding: '10px 8px' }}>
                      <StatusPill status={call.status} answered={!!call.answered} />
                    </td>
                    <td style={{ padding: '10px 8px' }}>{formatDuration(call.duration)}</td>
                    <td style={{ padding: '10px 8px', textTransform: 'capitalize' }}>
                      {call.direction || 'outgoing'}
                    </td>
                    <td style={{ padding: '10px 8px' }}>{call.ops_user_name || '—'}</td>
                    <td style={{ padding: '10px 8px' }}>
                      {call.recording_s3_key ? (
                        <AudioPlayer callUuid={call.uuid} />
                      ) : (
                        <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default CallHistoryModal;
